import type {PosterUiDelegate} from '@PosterWhiteboard/ui/poster-ui.types';
import type {GetTeamCreditsResponse} from '@Libraries/user.library';

let registeredDelegate: PosterUiDelegate | null = null;
const warnedMethods = new Set<keyof PosterUiDelegate>();

const warnUnregistered = (method: keyof PosterUiDelegate): void => {
  if (!warnedMethods.has(method)) {
    warnedMethods.add(method);
    console.warn(`PosterUi delegate is not registered; '${method}' was a no-op. Expected in headless/generation contexts only.`);
  }
};

/**
 * Headless contexts have no credits UI to refresh; the zeroed shape only exists so callers
 * that fire-and-forget a store refresh get a resolved promise of the right type.
 */
export const EMPTY_TEAM_CREDITS: GetTeamCreditsResponse = {
  credits: 0,
  freeCredits: 0,
  freeCreditsExpiryDate: null,
  legacyCredits: 0,
  legacyCreditsExpiryDate: null,
  purchasedCredits: 0,
  renewalCredits: null,
  renewalDate: null,
};

const nullPosterUiDelegate: PosterUiDelegate = {
  openMessageModal: (): void => {
    warnUnregistered('openMessageModal');
  },
  openErrorModal: (): void => {
    warnUnregistered('openErrorModal');
  },
  openPosterInGenerationWarningModal: (): void => {
    warnUnregistered('openPosterInGenerationWarningModal');
  },
  openNewDownloadModal: (): void => {
    warnUnregistered('openNewDownloadModal');
  },
  openUserSaveConflictModal: (): void => {
    warnUnregistered('openUserSaveConflictModal');
  },
  showMessageGrowl: (): void => {
    warnUnregistered('showMessageGrowl');
  },
  showEditorNotificationMessageGrowl: (): void => {
    warnUnregistered('showEditorNotificationMessageGrowl');
  },
  hideMessageGrowl: (): void => {
    warnUnregistered('hideMessageGrowl');
  },
  removeMessageGrowl: (): void => {
    warnUnregistered('removeMessageGrowl');
  },
  runPendingLayersFlow: (): Promise<void> => {
    warnUnregistered('runPendingLayersFlow');
    return Promise.resolve();
  },
  runMdePendingFlow: (): Promise<void> => {
    warnUnregistered('runMdePendingFlow');
    return Promise.resolve();
  },
  showLayeredPosterFeedback: (): void => {
    warnUnregistered('showLayeredPosterFeedback');
  },
  showMdeFeedback: (): void => {
    warnUnregistered('showMdeFeedback');
  },

  openEditMenuModal: (): void => {
    warnUnregistered('openEditMenuModal');
  },
  openEditScheduleModal: (): void => {
    warnUnregistered('openEditScheduleModal');
  },
  openEditTableContentModal: (): void => {
    warnUnregistered('openEditTableContentModal');
  },
  openEditTableModal: (): void => {
    warnUnregistered('openEditTableModal');
  },
  openPosterEditorEditFancyTextModal: (): void => {
    warnUnregistered('openPosterEditorEditFancyTextModal');
  },
  openPosterEditorItemEffectsModal: (): void => {
    warnUnregistered('openPosterEditorItemEffectsModal');
  },
  openPosterEditorMoreOptionsModal: (): void => {
    warnUnregistered('openPosterEditorMoreOptionsModal');
  },
  closePosterEditorMoreOptionsModal: (): void => {
    warnUnregistered('closePosterEditorMoreOptionsModal');
  },
  openPosterEditorPageOptionsModal: (): void => {
    warnUnregistered('openPosterEditorPageOptionsModal');
  },
  openPosterEditorQRContentModal: (): void => {
    warnUnregistered('openPosterEditorQRContentModal');
  },
  openPosterEditorTabsTextModal: (): void => {
    warnUnregistered('openPosterEditorTabsTextModal');
  },
  openPosterEditorCropModal: (): void => {
    warnUnregistered('openPosterEditorCropModal');
  },
  openReplaceMediaModal: (): void => {
    warnUnregistered('openReplaceMediaModal');
  },
  onOpenMaskingModal: (): Promise<void> => {
    warnUnregistered('onOpenMaskingModal');
    return Promise.resolve();
  },

  notifyGettyLimitReached: (): void => {
    warnUnregistered('notifyGettyLimitReached');
  },
  onAddItems: (): Promise<void> => {
    warnUnregistered('onAddItems');
    return Promise.resolve();
  },

  // 0 = nothing to charge: the removal flows are user-triggered and never run headless.
  getCreditsCostForVideoBackgroundRemoval: (): Promise<number> => {
    warnUnregistered('getCreditsCostForVideoBackgroundRemoval');
    return Promise.resolve(0);
  },
  onCreditsPurchaseSuccess: (): void => {
    warnUnregistered('onCreditsPurchaseSuccess');
  },
  refreshTeamCreditsInStore: (): Promise<GetTeamCreditsResponse> => {
    warnUnregistered('refreshTeamCreditsInStore');
    return Promise.resolve(EMPTY_TEAM_CREDITS);
  },
  triggerBuyMoreCredits: (): void => {
    warnUnregistered('triggerBuyMoreCredits');
  },
  updateVideoBgRemovalCredits: (): void => {
    warnUnregistered('updateVideoBgRemovalCredits');
  },

  // `true` means "still on its own page's canvas", so no cross-page move flow ever
  // triggers without the editor DOM the real check measures against.
  isFabricObjectOnPageCanvas: (): boolean => {
    warnUnregistered('isFabricObjectOnPageCanvas');
    return true;
  },
  // `false` = scroll layout: every page stays visible, matching headless rendering
  // where no page-switching UI exists.
  isSinglePageMode: (): boolean => {
    warnUnregistered('isSinglePageMode');
    return false;
  },
  // `false`: the embedded morph editor is a registered-UI context, never headless.
  isEmbeddedMorphModeActive: (): boolean => {
    warnUnregistered('isEmbeddedMorphModeActive');
    return false;
  },
  // `false`: without a modal container nothing can block page navigation.
  isPageNavigationBlockedByModal: (): boolean => {
    warnUnregistered('isPageNavigationBlockedByModal');
    return false;
  },
  // `undefined` mirrors the real implementation's refusal path when the per-page
  // controls DOM is absent: the caller restores the item's original position.
  moveItemToDifferentPage: (): Promise<string | undefined> => {
    warnUnregistered('moveItemToDifferentPage');
    return Promise.resolve(undefined);
  },
  pasteStylesOnMouseDown: (): Promise<void> => {
    warnUnregistered('pasteStylesOnMouseDown');
    return Promise.resolve();
  },
  removeBgOnMouseDown: (): Promise<void> => {
    warnUnregistered('removeBgOnMouseDown');
    return Promise.resolve();
  },
  scrollToPage: (): void => {
    warnUnregistered('scrollToPage');
  },

  enterMediaCropMode: (): void => {
    warnUnregistered('enterMediaCropMode');
  },
  exitMediaCropMode: (): void => {
    warnUnregistered('exitMediaCropMode');
  },
  setImageBgRemovalCreditsCost: (): void => {
    warnUnregistered('setImageBgRemovalCreditsCost');
  },
  setMediaCropAspectRatio: (): void => {
    warnUnregistered('setMediaCropAspectRatio');
  },
  setMediaCropFrames: (): void => {
    warnUnregistered('setMediaCropFrames');
  },
  setMediaCropRotation: (): void => {
    warnUnregistered('setMediaCropRotation');
  },
  setPosterOverlayLoaderVisibility: (): void => {
    warnUnregistered('setPosterOverlayLoaderVisibility');
  },
  showInitalPosterItemsLoadError: (): void => {
    warnUnregistered('showInitalPosterItemsLoadError');
  },
  updateImagePopUpState: (): void => {
    warnUnregistered('updateImagePopUpState');
  },
  updateKeyboardShift: (): void => {
    warnUnregistered('updateKeyboardShift');
  },
  updatePopUpStateAndPosition: (): void => {
    warnUnregistered('updatePopUpStateAndPosition');
  },
  updateSpellCheckPopUpOpenState: (): void => {
    warnUnregistered('updateSpellCheckPopUpOpenState');
  },
  updateSpellCheckPopUpTargetAndWords: (): void => {
    warnUnregistered('updateSpellCheckPopUpTargetAndWords');
  },
  updateUnlinkedMediaState: (): void => {
    warnUnregistered('updateUnlinkedMediaState');
  },
};

export const registerPosterUiDelegate = (delegate: PosterUiDelegate): void => {
  registeredDelegate = delegate;
};

export const getPosterUi = (): PosterUiDelegate => {
  return registeredDelegate ?? nullPosterUiDelegate;
};
